import { z } from 'zod';
import { loginRequestSchema, loginResponseSchema } from './auth.js';
import { acceptMemberInviteRequestSchema } from './member.js';
import { signupRequestSchema } from './signup.js';
/** The identity providers behind "Continue with …". Mirrors Prisma `FederatedProvider`
 *  (lock-step test, S50-kqp9-TASK-048). Phase 2a enables only GOOGLE. */
export const FEDERATED_PROVIDERS = ['GOOGLE', 'MICROSOFT', 'APPLE', 'ENTERPRISE'];
export const federatedProviderSchema = z.enum(FEDERATED_PROVIDERS);
/** The URL / API spelling of a provider: `/api/web/auth/federated/google/start`, `/auth/google/callback`. */
export const FEDERATED_PROVIDER_SLUGS = ['google', 'microsoft', 'apple', 'enterprise'];
export const federatedProviderSlugSchema = z.enum(FEDERATED_PROVIDER_SLUGS);
/** The providers with a shared "Continue with …" button. `enterprise` is never one (owner decision (a)8, ADR 0042 decision 35). */
export const SHARED_FEDERATED_PROVIDER_SLUGS = ['google', 'microsoft', 'apple'];
export function isSharedFederatedProviderSlug(slug) {
    return SHARED_FEDERATED_PROVIDER_SLUGS.includes(slug);
}
/** 'google' → 'GOOGLE'. Total over the tuple. */
export function federatedProviderOfSlug(slug) {
    return slug.toUpperCase();
}
/** 'GOOGLE' → 'google'. Total over the tuple. */
export function federatedSlugOfProvider(provider) {
    return provider.toLowerCase();
}
/**
 * Why a federated round trip was started.
 * 'company_test' = an admin's company sign-in test (S50-kqp9 Phase 4a, ADR 0042 decision 5); never a session.
 */
export const FEDERATED_SIGN_IN_PURPOSES = ['sign_in', 'invite', 'signup', 'company_test'];
/** The purposes a browser may name on `/start` ('invite' has its own start route). */
export const FEDERATED_START_PURPOSES = ['sign_in', 'signup'];
/** The provider redirects here, on the CENTRAL sign-in origin only (ADR 0039): '/auth/google/callback'. */
export function federatedCallbackPath(slug) {
    return `/auth/${slug}/callback`;
}
/** The central app's "Sign-in methods" page. */
export const SIGN_IN_METHODS_PATH = '/sign-in-methods';
/** A sealed state (and the server-side PKCE verifier inside it) is good for this long. */
export const FEDERATED_STATE_TTL_SECONDS = 600;
/** AES-GCM sealed, base64url (S50-kqp9-TASK-052). Opaque to every client. */
export const federatedStateSchema = z.string().regex(/^[A-Za-z0-9_-]{40,2048}$/);
/** `GET /api/web/auth/federated/providers` — the providers this deployment has switched on. */
export const federatedProvidersDTOSchema = z
    .object({ providers: z.array(z.enum(SHARED_FEDERATED_PROVIDER_SLUGS)).max(SHARED_FEDERATED_PROVIDER_SLUGS.length) })
    .strict();
/** `POST /api/web/auth/federated/:provider/start`. `returnTo` is NOT here: the browser keeps the
 *  central page's own request and resumes Phase 1's handoff after the callback (ADR 0039). */
export const startFederatedSignInSchema = z
    .object({
    /** The email typed on the central page, passed to the provider as `login_hint`. */
    loginHint: z.string().trim().email().max(320).optional(),
    tenantSlug: loginRequestSchema.shape.tenantSlug,
    /** Absent = 'sign_in'. 'signup' = a new customer on /signup (Phase 2b). */
    purpose: z.enum(FEDERATED_START_PURPOSES).optional(),
})
    .strict();
/** `POST /api/web/auth/federated/:provider/invite/start` — the emailed invitation pair. */
export const startFederatedInviteSchema = acceptMemberInviteRequestSchema
    .pick({ email: true, token: true })
    .strict();
/** Both starts answer where to send the browser. */
export const federatedAuthorizeDTOSchema = z.object({ authorizeUrl: z.string().url() }).strict();
/** `POST /api/web/auth/federated/:provider/complete` — the callback page's two query values. */
export const completeFederatedSignInSchema = z
    .object({ state: federatedStateSchema, code: z.string().min(1).max(2048) })
    .strict();
/** Same shape as a password login: the central session cookie is set alongside. */
export const completeFederatedSignInDTOSchema = loginResponseSchema;
/** One linked provider on `GET /api/web/auth/sign-in-methods`. Never the provider subject (S7). */
export const signInMethodDTOSchema = z
    .object({
    provider: federatedProviderSlugSchema,
    /** The provider-proven address at link time — the caller's own. */
    email: z.string().email(),
    /** True when the provider asserted a company domain (Google `hd`). */
    companyAccount: z.boolean(),
    linkedAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
})
    .strict();
export const signInMethodsDTOSchema = z
    .object({ password: z.boolean(), providers: z.array(signInMethodDTOSchema) })
    .strict();
/** The central app's organization + Terms step of a provider sign-up. */
export const FEDERATED_SIGNUP_PATH = '/signup/organization';
/** A pending provider sign-up (and its `inx_fed_signup` cookie) lives this long. */
export const FEDERATED_SIGNUP_TTL_SECONDS = 900;
/** `GET /api/web/auth/federated/signup` — the caller's own pending sign-up. Never the subject (S7). */
export const pendingFederatedSignupDTOSchema = z
    .object({
    provider: federatedProviderSlugSchema,
    /** The provider-proven address the account will be created with. */
    email: z.string().email(),
    companyAccount: z.boolean(),
    expiresAt: z.string().datetime(),
})
    .strict();
/** `POST /api/web/auth/federated/:provider/signup/complete`. An identity that already matches a
 *  login signs in (never a second account); otherwise a pending sign-up is kept. */
export const completeFederatedSignupDTOSchema = z.discriminatedUnion('outcome', [
    z.object({ outcome: z.literal('signed_in'), session: loginResponseSchema }).strict(),
    z.object({ outcome: z.literal('pending'), pendingSignup: pendingFederatedSignupDTOSchema }).strict(),
]);
/** `POST /api/web/auth/federated/signup` — the organization + Terms step. No email (the provider
 *  proved it) and no password (there is none). */
export const finishFederatedSignupSchema = signupRequestSchema
    .pick({ name: true, tenantName: true, phone: true, timezone: true, acceptTerms: true, captchaToken: true })
    .strict();
export const finishFederatedSignupDTOSchema = z
    .object({
    /** Null while the deployment still requires a phone code before the account is active, or (phoneCodeSent
     *  false) when the provider identity was just bound to the new account and the person must continue with
     *  the provider once more to get a session (ADR 0039 decision 10). */
    session: loginResponseSchema.nullable(),
    /** True when a text-message code went out; the SPA goes to /verify-phone next. */
    phoneCodeSent: z.boolean(),
})
    .strict();
// S50-kqp9 Phase 3 — Continue with Microsoft (ADR 0040).
/** Microsoft's consumer ("personal account") tenant: the `tid` of every Outlook / Hotmail sign-in (ADR 0040 decision 5). */
export const MICROSOFT_CONSUMER_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';
/** The central app's "Confirm it's you" step: '/auth/microsoft/confirm'. */
export function federatedConfirmPath(slug) {
    return `/auth/${slug}/confirm`;
}
/** A pending link (its `inx_fed_link` cookie) and its emailed code live this long. Equals the code TTL. */
export const FEDERATED_LINK_CONFIRM_TTL_SECONDS = 900;
/** `POST /api/web/auth/federated/:provider/confirm` — the six digits from the email. */
export const confirmFederatedLinkSchema = z
    .object({ code: z.string().trim().regex(/^\d{6}$/) })
    .strict();
/** Same as a completed sign-in: the central session cookie is set alongside. */
export const confirmFederatedLinkDTOSchema = loginResponseSchema;
/** `POST /api/web/auth/federated/:provider/confirm/resend` takes no body. */
export const resendFederatedLinkCodeSchema = z.object({}).strict();
//# sourceMappingURL=federation.js.map